/**
 * Bill Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  AgentReviewSummary,
  BillEntity,
  BillDataEntity,
  LineItemEntity,
  BillsResultEntity,
  BillOperationResult,
  PaginationInfo,
} from "../../domain/entities/BillEntity";
import {
  BillDTO,
  BillDataDTO,
  LineItemDTO,
  BillsResponseDTO,
  BillOperationResponseDTO,
  PaginationDTO,
} from "../models/BillDTOs";
import { parseBillJson } from "../api/billsApi";

/** Coerce API value (string or number) to number for tcs_tax_percentage */
function toTcsPercentNumber(raw: unknown): number | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return !Number.isNaN(n) && n >= 0 ? n : undefined;
}

export class BillMapper {
  /**
   * Map LineItemDTO to LineItemEntity
   */
  static toLineItemEntity(dto: LineItemDTO): LineItemEntity {
    return {
      name: dto.name,
      description: dto.description,
      sku: dto.sku,
      hsn_or_sac: dto.hsn_or_sac,
      hsn_description: dto.hsn_description,
      quantity: dto.quantity,
      rate: dto.rate,
      item_total: dto.item_total,
      item_order: dto.item_order,
      unit: dto.unit as any,
      account_id: dto.account_id,
      original_account_id: dto.original_account_id,
      account_name: dto.account_name,
      tax_id: dto.tax_id,
      tax_name: dto.tax_name,
      tax_percentage: dto.tax_percentage,
      tax_amount: dto.tax_amount,
      item_total_inclusive_of_tax: dto.item_total_inclusive_of_tax,
      is_billable: dto.is_billable,
      gl_recommendation: dto.gl_recommendation,
      gl_confidence: dto.gl_confidence,
      gl_source_layer: dto.gl_source_layer,
      tds_tax_id: dto.tds_tax_id,
      tds_tax_percentage: dto.tds_tax_percentage,
      tds_tax_name: dto.tds_tax_name,
    };
  }

  /**
   * Map LineItemEntity to LineItemDTO
   */
  static toLineItemDTO(entity: LineItemEntity): LineItemDTO {
    return {
      name: entity.name,
      description: entity.description,
      sku: entity.sku,
      hsn_or_sac: entity.hsn_or_sac,
      hsn_description: entity.hsn_description,
      quantity: entity.quantity,
      rate: entity.rate,
      item_total: entity.item_total,
      item_order: entity.item_order,
      unit: entity.unit,
      account_id: entity.account_id,
      original_account_id: entity.original_account_id,
      account_name: entity.account_name,
      tax_id: entity.tax_id,
      tax_name: entity.tax_name,
      tax_percentage: entity.tax_percentage,
      tax_amount: entity.tax_amount,
      item_total_inclusive_of_tax: entity.item_total_inclusive_of_tax,
      is_billable: entity.is_billable,
      gl_recommendation: entity.gl_recommendation,
      gl_confidence: entity.gl_confidence,
      gl_source_layer: entity.gl_source_layer,
      tds_tax_id: entity.tds_tax_id,
      tds_tax_percentage: entity.tds_tax_percentage,
      tds_tax_name: entity.tds_tax_name,
    };
  }

  /**
   * Map BillDataDTO to BillDataEntity
   */
  static toBillDataEntity(dto: BillDataDTO): BillDataEntity {
    return {
      vendor_id: dto.vendor_id,
      // ZOUR-65: which GST registration this document is filed under.
      // These mappers are explicit field lists, so a field missing here is
      // silently dropped in BOTH directions — the save strips it and the load
      // never sees it, which is how a picked location vanished on reload while
      // the same PUT from curl worked.
      location_id: dto.location_id ?? null,
      // ZOUR-65 R11: the pipeline's location match. Same trap as location_id
      // (F9) — these mappers are explicit field lists, so leaving it out meant
      // the page never sent the stored match and the flags recomputed from
      // nothing every time.
      location_match: dto.location_match ?? null,
      vendor_name: dto.vendor_name,
      vendor_gstin: dto.vendor_gstin,
      vendor_address: dto.vendor_address,
      bill_number: dto.bill_number,
      invoice_number: dto.invoice_number,
      reference_number: dto.reference_number,
      date: dto.date,
      invoice_date: dto.invoice_date,
      due_date: dto.due_date,
      currency_id: dto.currency_id,
      currency_code: dto.currency_code,
      currency_symbol: dto.currency_symbol,
      exchange_rate: dto.exchange_rate,
      gst_no: dto.gst_no,
      gst_treatment: dto.gst_treatment as any,
      gst_type: dto.gst_type as BillDataEntity["gst_type"],
      is_igst: dto.is_igst,
      source_of_supply: dto.source_of_supply,
      destination_of_supply: dto.destination_of_supply,
      place_of_supply: dto.place_of_supply,
      customer_name: dto.customer_name,
      customer_gstin: dto.customer_gstin,
      eway_bill_number: dto.eway_bill_number,
      irn_number: dto.irn_number,
      line_items: (dto.line_items ?? []).map((item) =>
        this.toLineItemEntity(item),
      ),
      sub_total: dto.sub_total,
      tax_total: dto.tax_total,
      total: dto.total,
      discount: dto.discount,
      payment_terms: dto.payment_terms,
      payment_terms_label: dto.payment_terms_label,
      is_item_level_tax_calc: dto.is_item_level_tax_calc,
      is_inclusive_tax: dto.is_inclusive_tax,
      notes: dto.notes,
      terms: dto.terms,
      processing_metadata: dto.processing_metadata,
      validation_results: dto.validation_results,
      tds_applicable: dto.tds_applicable,
      tds_tax_id: dto.tds_tax_id,
      tds_tax_percentage: dto.tds_tax_percentage,
      tds_tax_name: dto.tds_tax_name,
      tcs_tax_id: dto.tcs_tax_id,
      tcs_tax_name: dto.tcs_tax_name,
      tcs_tax_percentage: toTcsPercentNumber(
        (dto as { tcs_tax_percentage?: unknown }).tcs_tax_percentage
      ),
      tcs_tax_amount: dto.tcs_tax_amount,
      tcs_nature_of_collection: dto.tcs_nature_of_collection,
      tds_tax_amount: dto.tds_tax_amount,
    };
  }

  /**
   * Map BillDataEntity to BillDataDTO
   */
  static toBillDataDTO(entity: BillDataEntity): BillDataDTO {
    return {
      vendor_id: entity.vendor_id,
      // ZOUR-65: see the note on the entity mapper — omit this and every UI
      // save strips the location before it reaches the API.
      location_id: entity.location_id ?? null,
      // ZOUR-65 R11: see the note on the entity mapper.
      location_match: entity.location_match ?? null,
      vendor_name: entity.vendor_name,
      vendor_gstin: entity.vendor_gstin,
      vendor_address: entity.vendor_address,
      bill_number: entity.bill_number,
      invoice_number: entity.invoice_number,
      reference_number: entity.reference_number,
      date: entity.date,
      invoice_date: entity.invoice_date,
      due_date: entity.due_date,
      currency_id: entity.currency_id,
      currency_code: entity.currency_code,
      currency_symbol: entity.currency_symbol,
      exchange_rate: entity.exchange_rate,
      gst_no: entity.gst_no,
      gst_treatment: entity.gst_treatment,
      gst_type: entity.gst_type,
      is_igst: entity.is_igst,
      source_of_supply: entity.source_of_supply,
      destination_of_supply: entity.destination_of_supply,
      place_of_supply: entity.place_of_supply,
      customer_name: entity.customer_name,
      customer_gstin: entity.customer_gstin,
      eway_bill_number: entity.eway_bill_number,
      irn_number: entity.irn_number,
      line_items: (entity.line_items ?? []).map((item) =>
        this.toLineItemDTO(item),
      ),
      sub_total: entity.sub_total,
      tax_total: entity.tax_total,
      total: entity.total,
      discount: entity.discount,
      payment_terms: entity.payment_terms,
      payment_terms_label: entity.payment_terms_label,
      is_item_level_tax_calc: entity.is_item_level_tax_calc,
      is_inclusive_tax: entity.is_inclusive_tax,
      notes: entity.notes,
      terms: entity.terms,
      processing_metadata: entity.processing_metadata,
      validation_results: entity.validation_results,
      tds_applicable: entity.tds_applicable,
      tds_tax_id: entity.tds_tax_id,
      tds_tax_percentage: entity.tds_tax_percentage,
      tds_tax_name: entity.tds_tax_name,
      tcs_tax_id: entity.tcs_tax_id,
      tcs_tax_name: entity.tcs_tax_name,
      tcs_tax_percentage: entity.tcs_tax_percentage,
      tcs_tax_amount: entity.tcs_tax_amount,
      tcs_nature_of_collection: entity.tcs_nature_of_collection,
      tds_tax_amount: entity.tds_tax_amount,
    };
  }

  /**
   * Map BillDTO to BillEntity
   * Handles string or object bill_json from API
   */
  static toBillEntity(dto: BillDTO): BillEntity {
    // Parse bill_json if it's a string
    const billJsonParsed =
      typeof dto.bill_json === "string"
        ? parseBillJson(dto.bill_json)
        : dto.bill_json;

    return {
      id: dto.id,
      jobid: dto.jobid,
      companyid: dto.companyid,
      original_filename: dto.original_filename,
      bill_json: billJsonParsed
        ? this.toBillDataEntity(billJsonParsed)
        : ({} as BillDataEntity),
      reviewed: dto.reviewed,
      status: dto.status,
      on_hold: Number(dto.on_hold ?? 0), // Default to 0 (not on hold) if undefined
      is_under_review:
        dto.is_under_review !== undefined ? Number(dto.is_under_review) : undefined,
      open_queries_count:
        dto.open_queries_count !== undefined
          ? Number(dto.open_queries_count)
          : undefined,
      source: dto.source,
      // ZOUR-19 (review 15/17): the `date` / `due_date` COLUMNS. This mapper
      // builds the entity field by field, so omitting them meant the list and
      // the review page never saw the column at all and fell back to the raw
      // JSON date — which is DD/MM/YYYY and formatted as "N/A". Third time a
      // field-by-field mapper has silently swallowed a new field on this
      // ticket; worth checking the others when adding one.
      date: (dto as { date?: string | null }).date ?? undefined,
      due_date: (dto as { due_date?: string | null }).due_date ?? undefined,
      zoho_billid: dto.zoho_billid,
      posted_to_zoho_date: dto.posted_to_zoho_date,
      deleted: dto.deleted,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      signed_url: dto.signed_url,
      signed_url_expires_at: dto.signed_url_expires_at,
      gst_type: dto.gst_type,
      processing_notes: dto.processing_notes,
      error_message: dto.error_message,
      extracted_data_confidence: dto.extracted_data_confidence,
      processing_duration: dto.processing_duration,
      retry_count: dto.retry_count,
      vendor_match_method: dto.vendor_match_method,
      vendor_match_details: dto.vendor_match_details,
      // ZOUR-67 (change 7) — the fourth field this mapper would have swallowed,
      // per the note above. Cast rather than validated: the backend owns the
      // shape and re-checking it here would be a second, drifting definition.
      agent_review:
        (dto as { agent_review?: AgentReviewSummary | null }).agent_review ?? null,
    };
  }

  /**
   * Map BillEntity to BillDTO
   */
  static toBillDTO(entity: BillEntity): BillDTO {
    return {
      id: entity.id,
      jobid: entity.jobid,
      companyid: entity.companyid,
      original_filename: entity.original_filename,
      bill_json: this.toBillDataDTO(entity.bill_json),
      reviewed: entity.reviewed,
      status: entity.status,
      on_hold: entity.on_hold,
      is_under_review: entity.is_under_review,
      open_queries_count: entity.open_queries_count,
      zoho_billid: entity.zoho_billid,
      posted_to_zoho_date: entity.posted_to_zoho_date,
      deleted: entity.deleted,
      created_at: entity.created_at,
      updated_at: entity.updated_at,
      signed_url: entity.signed_url,
      signed_url_expires_at: entity.signed_url_expires_at,
      gst_type: entity.gst_type,
      processing_notes: entity.processing_notes,
      error_message: entity.error_message,
      extracted_data_confidence: entity.extracted_data_confidence,
      processing_duration: entity.processing_duration,
      retry_count: entity.retry_count,
    };
  }

  /**
   * Map PaginationDTO to PaginationInfo
   */
  static toPaginationInfo(dto: PaginationDTO): PaginationInfo {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      has_more: dto.has_more,
      current_page: dto.current_page,
      total_pages: dto.total_pages,
    };
  }

  /**
   * Map BillsResponseDTO to BillsResultEntity
   */
  static toBillsResultEntity(dto: BillsResponseDTO): BillsResultEntity {
    return {
      bills: dto.data.bills.map((bill) => this.toBillEntity(bill)),
      pagination: this.toPaginationInfo(dto.data.pagination),
    };
  }

  /**
   * Map BillOperationResponseDTO to BillOperationResult
   */
  static toBillOperationResult(
    dto: BillOperationResponseDTO
  ): BillOperationResult {
    return {
      success: dto.success,
      message: dto.message,
      bill: dto.data ? this.toBillEntity(dto.data) : undefined,
      error: dto.error,
    };
  }
}
